import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { GlassCard } from '../../components/common/GlassCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Building2, User, Mail, Lock, Phone, CreditCard, Building, ArrowRight, Upload } from 'lucide-react';

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultRole = searchParams.get('role') || 'customer';
  const referralCode = searchParams.get('ref') || '';

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultRole);
  const [kycFile, setKycFile] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    phone: '',
    ic_number: '',
    bank_name: '',
    bank_account: '',
    bank_holder_name: '',
    referral_code: referralCode,
    agent_code: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!formData.ic_number) {
      toast.error('IC number is required for KYC');
      return;
    }

    if (activeTab === 'agent' && !formData.agent_code) {
      toast.error('Agent code is required');
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        role: activeTab,
      };
      delete submitData.confirmPassword;

      await register(submitData);
      toast.success('Registration successful! Please upload your KYC document.');
      navigate('/profile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 py-12" data-testid="register-page">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1737823934403-c43c450f25de?crop=entropy&cs=srgb&fm=jpg&q=85)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 to-slate-950" />

      <div className="relative z-10 w-full max-w-lg">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <Building2 className="w-10 h-10 text-emerald-500" />
            <span className="text-2xl font-bold text-gradient-gold">SAM Rumah</span>
          </Link>
          <h1 className="text-2xl font-bold text-white">Create Account</h1>
          <p className="text-slate-400 mt-1">Join our premium property platform</p>
        </div>

        <GlassCard>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-slate-800/50 mb-6">
              <TabsTrigger value="customer" data-testid="tab-customer" className="data-[state=active]:bg-emerald-500">
                <User className="w-4 h-4 mr-2" />
                Buyer
              </TabsTrigger>
              <TabsTrigger value="agent" data-testid="tab-agent" className="data-[state=active]:bg-emerald-500">
                <Building className="w-4 h-4 mr-2" />
                Agent
              </TabsTrigger>
            </TabsList>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-slate-300">Full Name (as per IC)</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      name="full_name"
                      placeholder="Your full name"
                      value={formData.full_name}
                      onChange={handleChange}
                      className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                      required
                      data-testid="register-fullname"
                    />
                  </div>
                </div>

                <div className="col-span-2">
                  <Label className="text-slate-300">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                      required
                      data-testid="register-email"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300">Phone</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      name="phone"
                      placeholder="+60123456789"
                      value={formData.phone}
                      onChange={handleChange}
                      className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                      required
                      data-testid="register-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300">IC Number</Label>
                  <div className="relative mt-1">
                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      name="ic_number"
                      placeholder="123456-12-1234"
                      value={formData.ic_number}
                      onChange={handleChange}
                      className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                      required
                      data-testid="register-ic"
                    />
                  </div>
                </div>

                {activeTab === 'agent' && (
                  <div className="col-span-2">
                    <Label className="text-slate-300">Agent Code</Label>
                    <Input
                      name="agent_code"
                      placeholder="Your unique agent code"
                      value={formData.agent_code}
                      onChange={handleChange}
                      className="bg-slate-800/50 border-slate-700 text-white mt-1"
                      required
                      data-testid="register-agent-code"
                    />
                    <p className="text-xs text-slate-500 mt-1">Your agent code will be prefixed based on your rank</p>
                  </div>
                )}

                <div className="col-span-2">
                  <Label className="text-slate-300">Referral Code (Optional)</Label>
                  <Input
                    name="referral_code"
                    placeholder="Enter referral code if you have one"
                    value={formData.referral_code}
                    onChange={handleChange}
                    className="bg-slate-800/50 border-slate-700 text-white mt-1"
                    data-testid="register-referral"
                  />
                </div>

                <div className="col-span-2 border-t border-slate-700 pt-4 mt-2">
                  <p className="text-sm text-slate-400 mb-3">Bank Details (for withdrawals)</p>
                </div>

                <div>
                  <Label className="text-slate-300">Bank Name</Label>
                  <Input
                    name="bank_name"
                    placeholder="e.g., Maybank"
                    value={formData.bank_name}
                    onChange={handleChange}
                    className="bg-slate-800/50 border-slate-700 text-white mt-1"
                    data-testid="register-bank-name"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Bank Account No.</Label>
                  <Input
                    name="bank_account"
                    placeholder="Account number"
                    value={formData.bank_account}
                    onChange={handleChange}
                    className="bg-slate-800/50 border-slate-700 text-white mt-1"
                    data-testid="register-bank-account"
                  />
                </div>

                <div className="col-span-2">
                  <Label className="text-slate-300">Bank Account Holder Name</Label>
                  <Input
                    name="bank_holder_name"
                    placeholder="Must match IC name"
                    value={formData.bank_holder_name}
                    onChange={handleChange}
                    className="bg-slate-800/50 border-slate-700 text-white mt-1"
                    data-testid="register-bank-holder"
                  />
                </div>

                <div className="col-span-2 border-t border-slate-700 pt-4 mt-2">
                  <p className="text-sm text-slate-400 mb-3">Set your password</p>
                </div>

                <div>
                  <Label className="text-slate-300">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                      required
                      data-testid="register-password"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300">Confirm Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      name="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                      required
                      data-testid="register-confirm-password"
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-emerald-500 hover:bg-emerald-600 btn-glow mt-6"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </form>
          </Tabs>

          <div className="mt-6 text-center">
            <p className="text-slate-400 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-emerald-400 hover:text-emerald-300">
                Sign in
              </Link>
            </p>
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

export default Register;
